import { ChannelsApi } from '../contexts/distribution/channels/channels.api';
import { EntitiesApi } from '../contexts/content/entities/entities.api';
import { IdentityApi } from '../contexts/accounts/identity/identity.api';
import { SessionService } from '../contexts/accounts/identity/session.service';
import { MediaApi } from '../contexts/content/media/media.api';
import { PlansApi } from '../contexts/accounts/plans/plans.api';
import { PostsApi } from '../contexts/content/posts/posts.api';
import { PublishingApi } from '../contexts/distribution/publishing/publishing.api';
import { SchedulingApi } from '../contexts/distribution/scheduling/scheduling.api';
import { OperationsApi } from '../contexts/platform/operations/operations.api';
import { AdminApi } from '../contexts/platform/admin/admin.api';
import { ConfigApi } from '../contexts/platform/config/config.api';
import { PreferencesApi } from '../contexts/platform/user-preferences/preferences.api';
import { WorkspacesApi } from '../contexts/accounts/workspaces/workspaces.api';
import { TaskStore } from '../contexts/platform/tasks/model/task-store';
import { TasksApi } from '../contexts/platform/tasks/tasks.api';
import { ApiRoutes } from '@gr/core/api-routes';
import { RealtimeMessageType } from '@gr/core/platform/system/realtime';
import { ConfigService } from '../shared/config/config-service';
import { HttpClient } from '../shared/http/http-client';
import { I18nService } from '../shared/i18n/i18n';
import { RealtimeConnection, RealtimeStatus, type SocketFactory, type SocketLike } from '../shared/realtime/realtime-connection';
import { BrowserHistory, Router, type HistoryLike } from '../shared/router/router';
import { BrowserStorage, type KeyValueStorage } from '../shared/storage';
import { Observable } from '../shared/store';
import { ThemeService } from '../shared/theme/theme-service';
import { SessionStatus } from '../contexts/accounts/identity/session.service';
import { MESSAGES } from './messages';
import { ROUTES, type RouteKey } from './routes';

export interface AppServices {
  http: HttpClient;
  router: Router<RouteKey>;
  i18n: I18nService;
  theme: ThemeService;
  session: SessionService;
  /** Счётчик изменений справочников блога — навигация перечитывает их список. */
  catalogVersion: Observable<number>;
  /** Соединение событий (SYS-UC-13): открыто, пока пользователь вошёл. */
  realtime: RealtimeConnection;
  /** Публичная конфигурация платформы (CFG-UC-1): загружается при старте приложения. */
  config: ConfigService;
  /** Фоновые задачи вкладки (TASK-UC-2): по соединению событий и из ответов API. */
  tasks: TaskStore;
  api: {
    identity: IdentityApi;
    workspaces: WorkspacesApi;
    entities: EntitiesApi;
    posts: PostsApi;
    media: MediaApi;
    channels: ChannelsApi;
    scheduling: SchedulingApi;
    publishing: PublishingApi;
    operations: OperationsApi;
    admin: AdminApi;
    config: ConfigApi;
    preferences: PreferencesApi;
    plans: PlansApi;
    tasks: TasksApi;
  };
}

/** Адрес соединения событий на том же хосте, что и страница (ADR-012). */
function realtimeUrl(): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${ApiRoutes.system.realtime}`;
}

/** Браузерный WebSocket за узким интерфейсом SocketLike — его же подменяют тесты и витрина. */
const browserSockets: SocketFactory = (url) => {
  const ws = new WebSocket(url);
  const socket: SocketLike = { onopen: null, onclose: null, onmessage: null, send: (text) => ws.send(text), close: () => ws.close() };
  ws.onopen = () => socket.onopen?.();
  ws.onclose = (event) => socket.onclose?.({ code: event.code });
  ws.onmessage = (event) => socket.onmessage?.({ data: event.data });
  return socket;
};

/** Composition root фронтенда: единственное место, где создаются сервисы. */
export function createServices(
  storage: KeyValueStorage = new BrowserStorage(),
  history: HistoryLike = new BrowserHistory(),
  sockets: SocketFactory = browserSockets,
): AppServices {
  const http = new HttpClient();
  const config = new ConfigService(http);
  const router = new Router<RouteKey>(ROUTES, history);
  const i18n = new I18nService(MESSAGES, storage);
  const theme = new ThemeService(storage);
  const identity = new IdentityApi(http);
  const session = new SessionService(identity, i18n, theme);

  // IAM-UC-3.CC-1: 401 — сессия истекла; RequireAuth уведёт на вход с возвратом.
  http.onUnauthorized(() => session.expire());

  // SYS-UC-13: соединение событий живёт, пока пользователь вошёл; 4401 — та же истёкшая сессия.
  const realtime = new RealtimeConnection(realtimeUrl(), sockets, undefined, () => config.current.ui.realtime);
  // CFG-UC-1: администратор изменил публичный параметр — перечитать без перезагрузки. Пока
  // связи не было (обрыв, сон, перезапуск сервера), событие могло пройти мимо, поэтому
  // конфигурация перечитывается и при каждом открытии соединения.
  realtime.onMessage((message) => {
    if (message.type === RealtimeMessageType.ConfigChanged) void config.load();
  });
  realtime.status.subscribe(() => {
    if (realtime.status.get() === RealtimeStatus.Open) void config.load();
  });
  // TASK-UC-2: снимок задач на подписку и их изменения.
  const tasks = new TaskStore();
  realtime.onMessage((message) => tasks.apply(message));
  realtime.onUnauthorized = () => session.expire();
  session.state.subscribe(() => {
    const { status, me } = session.state.get();
    if (status === SessionStatus.Authenticated) realtime.start();
    else {
      realtime.stop();
      tasks.clear();
    }
    // PREF-UC-3.CC-2: у вошедшего — личные значения, до входа и после выхода — значения платформы.
    if (status !== SessionStatus.Loading) config.followUser(me?.user.id ?? null);
  });
  // SYS-UC-13.CC-1: вернулась сеть или вкладка — переподключиться сразу, не дожидаясь паузы.
  window.addEventListener('online', () => realtime.reconnectNow());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') realtime.reconnectNow();
  });

  return {
    http,
    router,
    i18n,
    theme,
    session,
    catalogVersion: new Observable(0),
    realtime,
    config,
    tasks,
    api: {
      identity,
      workspaces: new WorkspacesApi(http),
      entities: new EntitiesApi(http),
      posts: new PostsApi(http),
      media: new MediaApi(http),
      channels: new ChannelsApi(http),
      scheduling: new SchedulingApi(http),
      publishing: new PublishingApi(http),
      operations: new OperationsApi(http),
      admin: new AdminApi(http),
      config: new ConfigApi(http),
      preferences: new PreferencesApi(http),
      plans: new PlansApi(http),
      tasks: new TasksApi(http),
    },
  };
}
